import {
  createAsyncThunk,
  type ThunkDispatch,
  type UnknownAction
} from "@reduxjs/toolkit"
import type { BaseOptionType } from "antd/lib/select"

import { errorSave } from "../../../../redux/errors.slice"
import type { RootState } from "../../../../store.ts"
import { type BasePagination } from "../../../../types/api.ts"
import { unknownError } from "../../../../utils/handlers/handle-error"
import { orderByIds } from "../../../../utils/object/order-by-ids.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import { serializeData } from "../../../../utils/utils.ts"
import * as services from "../../api/project-milestones.services"
import { type ProjectMilestone } from "../../types/project-phases.types.tsx"
import {
  fetchFinancialSnapshot,
  fetchFinancialTransactionItems
} from "../project-finance/project-finance.thunks.ts"
import { projectSliceName } from "../project.constants.ts"
import { projectSetMilestoneOrder } from "../project.slice.ts"
import { type ProjectMilestoneFilters } from "../project.types.ts"
import {
  type MilestoneBulkResult,
  type ProjectMilestoneSnapshot
} from "./project-milestones.types.ts"

const refetchProjectFinance = (
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  getState: () => unknown,
  projectId?: string
) => {
  if (!projectId) return

  const { project } = getState() as {
    project?: {
      financialTransactionFilters?: Record<string, unknown>
      selectedSubcontractor?: {
        filter_params?: Record<string, unknown>
      } | null
    }
  }

  let params = {}
  if (project?.selectedSubcontractor?.filter_params) {
    params = project.selectedSubcontractor.filter_params
  } else {
    params = serializeData({ ...(project?.financialTransactionFilters ?? {}) })
  }

  dispatch(
    fetchFinancialTransactionItems({
      isMore: false,
      params: { project: projectId, ...params }
    })
  )
  dispatch(fetchFinancialSnapshot(projectId))
}

export const fetchProjectMilestones = createAsyncThunk<
  { isMore: boolean; data: BasePagination<ProjectMilestone> },
  {
    params: ProjectMilestoneFilters & { lead: string }
    isMore: boolean
  }
>(
  `${projectSliceName}/fetchProjectMilestones`,
  async ({ isMore, params }, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectMilestones(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return {
      data: response.data as BasePagination<ProjectMilestone>,
      isMore
    }
  }
)

// Its own slot: writing into `milestones` would replace the open tab's rows.
export const fetchAlertContributingMilestones = createAsyncThunk<
  ProjectMilestone[],
  string
>(
  `${projectSliceName}/fetchAlertContributingMilestones`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectMilestones({
      lead: projectId,
      page: 1,
      page_size: 100
    })

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return (response.data as BasePagination<ProjectMilestone>).results
  }
)

export const createProjectMilestone = createAsyncThunk<
  ProjectMilestone,
  Partial<ProjectMilestone>
>(
  `${projectSliceName}/createProjectMilestone`,
  async (data, { dispatch, getState, rejectWithValue }) => {
    const response = await services.createProjectMilestone(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Milestone created successfully.")

    const milestone = (response?.data?.details?.data ??
      response?.data) as ProjectMilestone
    refetchProjectFinance(dispatch, getState, milestone?.lead ?? data?.lead)

    return milestone
  }
)

export const patchProjectMilestone = createAsyncThunk<
  ProjectMilestone,
  Partial<ProjectMilestone> & { id: string; silent?: boolean }
>(
  `${projectSliceName}/patchProjectMilestone`,
  async ({ silent, ...data }, { dispatch, getState, rejectWithValue }) => {
    const response = await services.patchProjectMilestone(data)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    if (!silent) showResponseMessage(response, "Milestone updated successfully.")
    const res = (response?.data?.details?.data ??
      response?.data) as ProjectMilestone
    refetchProjectFinance(dispatch, getState, res?.lead ?? data?.lead)

    return { ...res, id: data?.id }
  }
)

// The milestones table is infinite-scroll (SCROLL_PAGE_SIZE = 50, accumulated
// client-side), but a post-write list refresh always dispatches isMore: false,
// which replaces state with just the first page — so a caller that needs one
// specific milestone's just-recomputed figures right after a write cannot
// assume it is still in that page. Reads the milestone by id directly instead.
export const fetchProjectMilestoneById = createAsyncThunk<
  ProjectMilestone,
  string
>(
  `${projectSliceName}/fetchProjectMilestoneById`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectMilestone(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as ProjectMilestone
  }
)

export const fetchProjectMilestonesFilterOptions = createAsyncThunk<
  Record<string, BaseOptionType[]>,
  { project_id: string }
>(
  `${projectSliceName}/fetchProjectMilestonesFilterOptions`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectMilestonesFilterOptions(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as Record<string, BaseOptionType[]>
  }
)

// export const fetchProjectMilestone = createAsyncThunk<ProjectMilestone, number>(
//   `${projectSliceName}/fetchProjectMilestone`,
//   async (id, { dispatch, rejectWithValue }) => {
//     const response = await services.getProjectMilestone(id)
//
//     if (!response.ok) {
//       dispatch(errorSave(response?.data?.details || unknownError))
//       return rejectWithValue(response.data)
//     }
//
//     return response?.data
//   }
// )

// export const updateProjectMilestone = createAsyncThunk<
//   ProjectMilestone,
//   { id: number; data: Partial<ProjectMilestone> }
// >(
//   `${projectSliceName}/updateProjectMilestone`,
//   async ({ id, data }, { dispatch, rejectWithValue }) => {
//     const response = await services.updateProjectMilestone(id, data)
//
//     if (!response.ok) {
//       dispatch(errorSave(response?.data?.details || unknownError))
//       return rejectWithValue(response.data)
//     }
//
//     showResponseMessage(response, "Milestone updated successfully.")
//
//     return response?.data?.details?.data ?? response?.data
//   }
// )

export const deleteProjectMilestone = createAsyncThunk<string, string>(
  `${projectSliceName}/deleteProjectMilestone`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.deleteProjectMilestone(id)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Milestone deleted successfully.")

    return id
  }
)

export const reorderProjectMilestones = createAsyncThunk<
  void,
  { id: string; order: number }[],
  { state: RootState }
>(
  `${projectSliceName}/reorderProjectMilestones`,
  async (items, { dispatch, getState, rejectWithValue }) => {
    const previous = getState()?.project?.milestones?.results || []

    // Move the rows before the request so a dragged milestone stays where it
    // was dropped; the list is refetched by its own flows afterwards.
    const optimistic = orderByIds(
      previous,
      items.map(({ id }) => id)
    )

    if (optimistic) dispatch(projectSetMilestoneOrder(optimistic))

    const response = await services.reorderProjectMilestones({ items })

    if (!response.ok) {
      dispatch(projectSetMilestoneOrder(previous))
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    showResponseMessage(response, "Milestones reordered successfully.")
  }
)

/** Pulls a human-readable reason out of whatever error envelope the API returned. */
const milestoneBulkErrorMessage = (data: unknown): string | undefined => {
  if (!data || typeof data !== "object") return undefined
  const body = data as Record<string, unknown>
  const detail = (body.details ?? body) as Record<string, unknown>
  const candidates = [detail?.message, detail?.detail, body?.detail, body?.message]
  return candidates.find((value): value is string => typeof value === "string")
}

const summarizeMilestoneBulk = (
  outcomes: { id: string; ok: boolean; message?: string }[],
  fallback: string
): MilestoneBulkResult => ({
  requested: outcomes.length,
  succeeded: outcomes.filter((outcome) => outcome.ok).map((outcome) => outcome.id),
  failed: outcomes
    .filter((outcome) => !outcome.ok)
    .map((outcome) => ({ id: outcome.id, message: outcome.message || fallback }))
})

// Milestones have no bulk endpoint, so both bulk actions fan the existing
// per-milestone calls out in parallel and aggregate the outcome. Deliberately
// no `errorSave` and no `refetchProjectFinance` here: a per-item failure is
// reported by the bulk bar, not the global toast, and the section refetches the
// list + finance snapshot once after the whole batch settles.
export const bulkPatchProjectMilestones = createAsyncThunk<
  MilestoneBulkResult,
  { ids: string[]; status: number }
>(
  `${projectSliceName}/bulkPatchProjectMilestones`,
  async ({ ids, status }) => {
    const outcomes = await Promise.all(
      ids.map(async (id) => {
        try {
          const response = await services.patchProjectMilestone({ id, status })
          return {
            id,
            ok: response.ok,
            message: response.ok
              ? undefined
              : milestoneBulkErrorMessage(response.data)
          }
        } catch {
          return { id, ok: false, message: undefined }
        }
      })
    )

    return summarizeMilestoneBulk(
      outcomes,
      "This milestone could not be updated."
    )
  }
)

export const bulkDeleteProjectMilestones = createAsyncThunk<
  MilestoneBulkResult,
  { ids: string[] }
>(`${projectSliceName}/bulkDeleteProjectMilestones`, async ({ ids }) => {
  const outcomes = await Promise.all(
    ids.map(async (id) => {
      try {
        const response = await services.deleteProjectMilestone(id)
        return {
          id,
          ok: response.ok,
          message: response.ok
            ? undefined
            : milestoneBulkErrorMessage(response.data)
        }
      } catch {
        return { id, ok: false, message: undefined }
      }
    })
  )

  return summarizeMilestoneBulk(outcomes, "This milestone could not be deleted.")
})

export const fetchProjectMilestoneSnapshot = createAsyncThunk<
  ProjectMilestoneSnapshot,
  string
>(
  `${projectSliceName}/fetchProjectMilestoneSnapshot`,
  async (projectId, { dispatch, rejectWithValue }) => {
    const response = await services.getProjectMilestoneSnapshot(projectId)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details || unknownError))
      return rejectWithValue(response.data)
    }

    return response.data as ProjectMilestoneSnapshot
  }
)
